// src/contexts/ThemeContext.tsx - account-synced with localStorage fallback
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import createAppTheme from '../theme/theme';
import { useAuth } from './AuthContext';
import { userPreferencesAPI } from '../services/api';

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  themeMode: ThemeMode;
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user, isAuthenticated, updateUserPrefs } = useAuth();

  // Seed from localStorage so the pre-login screen isn't stuck on a
  // hardcoded default - this is just a device-level "last known look",
  // not the source of truth once an account is involved.
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem('themeMode');
    return (savedMode as ThemeMode) || 'light';
  });

  // Once authenticated, the account's saved preference takes over as the
  // source of truth - and we mirror it into localStorage too, so the next
  // time this device shows the login screen (logged out) it still reflects
  // this user's last choice rather than reverting to a stale value.
  useEffect(() => {
    if (isAuthenticated && user?.themeMode) {
      setThemeModeState(user.themeMode);
      localStorage.setItem('themeMode', user.themeMode);
    }
  }, [isAuthenticated, user?.themeMode]);

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem('themeMode', mode);

    if (isAuthenticated && user) {
      // Applied to the persisted authUser blob immediately, not gated
      // behind the API call resolving - AuthContext rehydrates `user`
      // straight from that blob on the next mount (e.g. a page reload),
      // and this ThemeProvider's own sync effect above trusts
      // user.themeMode as authoritative once authenticated. If the
      // PUT hadn't resolved yet by the time a reload happened, that
      // effect would overwrite this mode back to whatever the stale
      // blob still said, silently reverting a change that had already
      // taken visible effect. Updating optimistically here is what
      // actually delivers the "local value still applies for this
      // session even if the account save fails" behavior the comment
      // below has always claimed - previously that was only true of
      // the in-memory React state, not of what a reload rehydrates from.
      updateUserPrefs({ themeMode: mode });

      // Full round-trip through the same preferences endpoint used by
      // ProfilePage - the DTO requires the complete set, so we carry
      // forward every other current value and only change themeMode.
      userPreferencesAPI
        .updateMessagePreferences({
          msgBackupAlerts: user.msgBackupAlerts,
          msgQuoteAlerts: user.msgQuoteAlerts,
          msgBookingReminders: user.msgBookingReminders,
          msgSystemToasts: user.msgSystemToasts,
          accessibilityMode: user.accessibilityMode,
          showPerformanceIndicator: user.showPerformanceIndicator,
          showOnboarding: user.showOnboarding ?? true,
          themeMode: mode,
          locale: user.locale,
        })
        .catch(() => {
          // Best-effort sync - the local/localStorage value still applies
          // for this session even if the account save fails.
        });
    }
  };

  const toggleTheme = () => {
    setThemeMode(themeMode === 'light' ? 'dark' : 'light');
  };

  const theme = useMemo(() => createAppTheme(themeMode), [themeMode]);

  const value = {
    themeMode,
    toggleTheme,
    setThemeMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// Export as AppThemeProvider for backwards compatibility
export const AppThemeProvider = ThemeProvider;

// Custom hook to use theme context
export const useAppTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within a ThemeProvider');
  }
  return context;
};
